// AppLayout.jsx
import React, { useEffect, useRef } from 'react'
import Header from './Header'
import Sidebar from './Sidebar'
import MainContent from './MainContent'
import { Box } from '@mui/material'
import { COLORS } from '../config/colors'
import './BackendToggle.css'
import { AgentPromptStateMachine } from '../utils/AgentPromptStateMachine'
import { API_CONFIG } from '../config/constants'
import useChatSessionManager from '../hooks/ChatSessionManager'
import useAudioRecording from '../hooks/useAudioRecording'
import useSSEMessageHandler from '../hooks/useSSEMessageHandler'
import useRoleTypeManager from '../hooks/useRoleTypeManager'
import useMatchResults from '../hooks/useMatchResults'
import useICPRequirements from '../hooks/useICPRequirements'
import useMessaging from '../hooks/useMessaging'
import useDiscoveryProgress from '../hooks/useDiscoveryProgress'
import RoleTypeSelection from './RoleTypeSelection'
import IntakeModeSelection from './IntakeModeSelection'
import ElevenLabsIntakePopover from './ElevenLabsIntakePopover'
import JDIntakePopover from './JDIntakePopover'
import EmptySearchStrategy from './EmptySearchStrategy'
import centralDiscoveryQueue from '../utils/centralDiscoveryQueue'
import { clearClientCaches, deleteIcp } from '../utils/api'

// Initialize the agent state machine
const agentStateMachine = new AgentPromptStateMachine()

const AppLayout = ({ userInfo, queries, initialSessionId }) => {
  // State for sidebar visibility
  const [leftSidebarOpen, setLeftSidebarOpen] = React.useState(true)
  const [rightSidebarOpen, setRightSidebarOpen] = React.useState(true)
  // Intake mode selection: 'topic' | 'elevenlabs' | null
  const [intakeMode, setIntakeMode] = React.useState(null)
  const [showIntakeModeModal, setShowIntakeModeModal] = React.useState(false)
  const [showElPopover, setShowElPopover] = React.useState(false)
  const [showJDPopover, setShowJDPopover] = React.useState(false)
  
  // Shims for legacy/unreachable code paths to satisfy parser after refactor
  // These prevent "not defined" errors for identifiers that remain in commented/unreachable code.
  let pendingId; // eslint-disable-line no-unused-vars
  let resp; // eslint-disable-line no-unused-vars
  
  // Flag to track if we've already started the conversation
  const hasStartedConversation = useRef(false)
  
  // Get user ID
  const userId = userInfo ? userInfo.uid : ''

  // Chat session management
  const {
    chatSessions,
    currentSessionId,
    messages,
    sessionName,
    fetchChatSessions,
    saveCurrentSession,
    loadChatSession,
    deleteChatSession,
    startNewSession,
    setMessages: originalSetMessages,
    setSessionName
  } = useChatSessionManager(API_CONFIG.url, userId)

  // Create a wrapper around setMessages that auto-saves when readonly components are added
  const setMessages = React.useCallback((updater) => {
    // Add detailed debugging to identify which code is calling setMessages
    const stack = new Error().stack;
    const stackLines = stack.split('\n');
    const relevantLines = stackLines.slice(1, 6).join('\n    ');
    console.log(`🔧 setMessages CALLED:`)
    console.log(`    Stack:`, relevantLines);
    
    // Also add a simple counter to track call order
    if (!window.setMessagesCallCount) window.setMessagesCallCount = 0;
    window.setMessagesCallCount++;
    console.log(`🔧 setMessages CALL #${window.setMessagesCallCount}`);
    
    originalSetMessages((prevMessages) => {
      console.log(`📊 setMessages: prev=${prevMessages.length}, readonly=${prevMessages.filter(msg => msg.sessionRestored === true).length}`);
      
      // If updater is a function, call it with prevMessages
      const newMessages = typeof updater === 'function' ? updater(prevMessages) : updater;
      
      // Check if we're about to lose readonly components
      const prevReadonly = prevMessages.filter(msg => msg.sessionRestored === true).length;
      const newReadonly = Array.isArray(newMessages) ? newMessages.filter(msg => msg.sessionRestored === true).length : 0;
      if (prevReadonly > 0 && newReadonly === 0) {
        console.log(`🚨 WARNING: About to LOSE ${prevReadonly} readonly components!`);
        console.log(`🚨 Previous messages:`, prevMessages.map(m => ({id: m.id, component: m.component, sessionRestored: m.sessionRestored})));
        console.log(`🚨 New messages:`, Array.isArray(newMessages) ? newMessages.map(m => ({id: m.id, component: m.component, sessionRestored: m.sessionRestored})) : newMessages);
      }
      
      // Check if any new messages have readonly components and auto-save session
      if (Array.isArray(newMessages)) {
        const hasNewReadonlyComponents = newMessages.some((msg, index) => {
          const isNewMessage = index >= prevMessages.length;
          const hasComponent = msg.component || msg._hasComponent;
          return isNewMessage && hasComponent;
        });
        
        if (hasNewReadonlyComponents) {
          console.log('🔄 Auto-saving session after adding readonly component messages via setMessages');
          
          // Check if any of the new readonly components are session-restored components
          // If so, don't auto-save to prevent infinite loop
          const hasSessionRestoredComponents = newMessages.some(msg => msg.sessionRestored === true);
          
          if (!hasSessionRestoredComponents) {
            console.log('💾 Auto-saving session - no session-restored components detected');
            // Use setTimeout to ensure the state update is processed first
            setTimeout(() => {
              console.log('💾 Calling saveCurrentSession with sessionName:', sessionName);
              // Collect component data and save session properly
              const componentData = getComponentData();
              saveCurrentSession(sessionName, sessionSearchId, null, currentDiscoveryState, componentData);
            }, 100);
          } else {
            console.log('🚫 Skipping auto-save - session-restored components detected (prevents infinite loop)');
          }
        }
      }
      
      console.log(`🔧 RETURNING FROM originalSetMessages:`, Array.isArray(newMessages) ? `${newMessages.length} messages` : 'non-array');
      return newMessages;
    });
  }, [originalSetMessages, sessionName, saveCurrentSession])

  // Debug messages state changes
  React.useEffect(() => {
    console.log(`📨 MESSAGES STATE CHANGED: length=${messages.length}, readonly count=${messages.filter(msg => msg.sessionRestored === true).length}`);
    console.log(`📨 MESSAGES REFERENCE:`, messages);
  }, [messages]);

  // Also check if we're actually passing the right messages to MainContent
  console.log(`🔄 APPLAYOUT RENDER: messages.length=${messages.length}, readonly=${messages.filter(msg => msg.sessionRestored === true).length}`);

  // SSE message handling
  const {
    currentAIMessage,
    isWaitingForResponse,
    setIsWaitingForResponse,
    handleSSEMessage,
    currentAIMessageRef,
    startTypingTimeout,
    clearTypingTimeout
  } = useSSEMessageHandler(
    // Custom setMessages function that prevents duplicate messages and auto-saves when readonly components are added
    (updater) => {
      setMessages((prevMessages) => {
        // If updater is a function, call it with prevMessages
        const newMessages = typeof updater === 'function' ? updater(prevMessages) : updater;
        
        // If newMessages is an array (direct assignment), check for duplicates
        if (Array.isArray(newMessages)) {
          
          // Filter out duplicate messages
          const uniqueMessages = [];
          const seen = new Set();
          
          for (const msg of newMessages) {
            // Create a key based on role and content
            const key = `${msg.role}:${msg.content}`;
            
            // Skip "Typing..." messages from duplicate detection
            // ALSO skip readonly components from duplicate detection
            if (msg.content === 'Typing...' || msg.sessionRestored === true || !seen.has(key)) {
              seen.add(key);
              uniqueMessages.push(msg);
            } else {
              console.log('Filtered out duplicate message:', msg.content.substring(0, 20) + '...');
            }
          }
          
          
          // Check if any new messages have readonly components and auto-save session
          const hasNewReadonlyComponents = uniqueMessages.some((msg, index) => {
            const isNewMessage = index >= prevMessages.length;
            const hasComponent = msg.component || msg._hasComponent;
            return isNewMessage && hasComponent;
          });
          
          if (hasNewReadonlyComponents) {
            console.log('Auto-saving session after adding readonly component messages');
            // Use setTimeout to ensure the state update is processed first
            setTimeout(() => {
              const componentData = getComponentData();
              saveCurrentSession(sessionName, sessionSearchId, null, currentDiscoveryState, componentData);
            }, 100);
          }
          
          return uniqueMessages;
        }
        
        // If updater is a function that modifies the array (e.g., adding a message)
        // We need to check the result for duplicates
        return newMessages;
      });
    }, 
    false, 
    saveCurrentSession
  ) // Set isAudioReady to false to disable voice response

  // Role type management
  const {
    selectedRoleTypes,
    showRoleModal,
    convoStarted,
    setConvoStarted,
    handleStartIntake,
    handleRoleTypeSelect: originalHandleRoleTypeSelect,
    handleStartConversation,
    resetRoleTypeSelection,
    setShowRoleModal,
    roleTypesCache,
    currentDiscoveryState,
    updateDiscoveryState,
    loadSessionState,
    advanceToNextTopic
  } = useRoleTypeManager(setMessages, saveCurrentSession, setSessionName)

  // Wrapper to pass userId to handleRoleTypeSelect
  const handleRoleTypeSelect = (roleTypes, options = {}) => {
    // Forward both userId and options to original so we can use silent=true
    return originalHandleRoleTypeSelect(roleTypes, userId, options);
  }

  // Centralized ICP requirements management
  const {
    icpRequirements,
    updateRequirements,
    getAllRequirements,
    clearRequirements
  } = useICPRequirements()
  
  // Discovery progress management
  const {
    discoveryProgress,
    isLoading: discoveryLoading,
    error: discoveryError,
    isSaving: discoverySaving,
    updateDiscovery,
    markTopicComplete,
    markTopicInProgress,
    getICPRequirements: getDiscoveryICPRequirements,
    getCompletionPercentage,
    getNextTopic,
    isTopicComplete,
    forceSave: forceDiscoverySave,
    mergeUpdates: mergeDiscoveryUpdates,
    resetProgress: resetDiscoveryProgress,
    DISCOVERY_STATUS
  } = useDiscoveryProgress(
    currentSessionId, 
    icpRequirements, // Pass initial ICP requirements
    currentDiscoveryState?.discoveryOrder || [] // Pass discovery order
  )
  
  // Match results management
  const {
    matchResults,
    matchCount,
    matchesLoading,
    sessionSearchId,
    handleRefreshResults: refreshResults,
    handleRefreshResultsFromQueue,
    saveOpenSearch,
    resetMatchResults,
    setSessionSearchId,
    openFindemSearch
  } = useMatchResults({ icpRequirements })
  
  // Wrapper for handleRefreshResults to pass messages and options (e.g., intakeType)
  const handleRefreshResults = (messagesWithCurrentText, preloadedResults, aiOptions) => {
    return refreshResults(messagesWithCurrentText || messages, preloadedResults, aiOptions);
  }

  // Messaging functionality
  const {
    usePythonBackend,
    setUsePythonBackend,
    handleSendText,
    handleTranscriptionComplete,
    experienceHistogramData,
    levelCounts,
    locationMap,
    locationKeys,
    companyStageData,
    companyBusinessModelData,
    titleKeys,
    setTitleKeys,
    selectedSkills,
    setSelectedSkills,
    confirmedSkills,
    setConfirmedSkills
  } = useMessaging({
    setMessages,
    messages,
    currentSessionId,
    userId,
    queries,
    selectedRoleTypes,
    handleRoleTypeSelect,
    handleSSEMessage,
    isAudioReady: false, // Set isAudioReady to false to disable voice response
    saveCurrentSession,
    currentDiscoveryState, // Pass the current discovery state
    advanceToNextTopic, // Pass the function to advance to the next topic
    handleRefreshResults, // Pass the function to refresh match results
    matchResults, // Pass the match results
    icpRequirements, // Pass the centralized ICP requirements
    updateRequirements, // Pass the function to update ICP requirements
    getAllRequirements, // Pass the function to get all ICP requirements
    forcePythonBackend: false, // Force Python backend to be disabled
    discoveryProgress: {
      markTopicInProgress,
      markTopicComplete,
      isTopicComplete,
      updateDiscovery,
      getICPRequirements: getDiscoveryICPRequirements,
      getCompletionPercentage,
      getNextTopic,
      forceSave: forceDiscoverySave,
      mergeUpdates: mergeDiscoveryUpdates,
      resetProgress: resetDiscoveryProgress,
      DISCOVERY_STATUS
    }, // Pass discovery progress functions
    startTypingTimeout, // Pass timeout functions to prevent stuck typing states
    clearTypingTimeout
  })

  // Audio recording functionality
  const {
    isRecording,
    isAudioReady,
    setIsAudioReady,
    startRecording,
    stopRecording
  } = useAudioRecording(handleTranscriptionComplete)

  // Sidebar dimensions
  const drawerWidth = 250
  const collapsedWidth = 50
  // rightSidebarWidth is now managed in MainContent.jsx

  // Make central discovery queue available globally and fetch chat sessions on load
  useEffect(() => {
    // Make central discovery queue available globally for cleanup
    window.centralDiscoveryQueue = centralDiscoveryQueue;
    fetchChatSessions()
  }, [])

  // Expose a helper to ensure role type defaults to TECHNICAL and initializes discovery order
  useEffect(() => {
    window.ensureTechnicalRoleType = () => {
      try {
        // Use wrapper so userId is passed properly and keep it silent
        handleRoleTypeSelect(['TECHNICAL'], { silent: true });
      } catch (e) {
        console.warn('ensureTechnicalRoleType failed:', e?.message || e);
      }
    };
    return () => {
      try { delete window.ensureTechnicalRoleType; } catch (_) {}
    };
  }, [handleRoleTypeSelect])

  // Global handler for "Start with JD" on the landing screen and inline prompts
  useEffect(() => {
    window.handleStartWithJD = () => {
      try {
        // Pre-select default role type silently BEFORE opening JD popover
        originalHandleRoleTypeSelect(['TECHNICAL'], { silent: true });
      } catch (_) {}
      // Do NOT flip convoStarted nor intakeMode on open.
      // Keep user on the start screen until they click "I'm done".
      try { window.__intakeChoice = 'jd'; localStorage.setItem('intakeChoice', 'jd'); } catch (_) {}
      setShowJDPopover(true);
    };
    return () => {
      try { delete window.handleStartWithJD; } catch (_) {}
    };
  }, [originalHandleRoleTypeSelect, setConvoStarted])

  // Global handler to start legacy topic-based discovery directly from the landing screen
  useEffect(() => {
    window.handleStartTopicBased = () => {
      try {
        // Switch to topic-based discovery (legacy/structured)
        setIntakeMode('topic');
        // Close any other popovers if open
        setShowElPopover(false);
        setShowJDPopover(false);
        // Reset intake choice to prompt-based
        try { window.__intakeChoice = 'prompt'; localStorage.removeItem('intakeChoice'); } catch (_) {}
        // Show the role type modal to begin topic-based discovery
        setShowRoleModal(true);
        // Keep convoStarted false until role is selected
        setConvoStarted(false);
      } catch (_) {}
    };
    return () => {
      try { delete window.handleStartTopicBased; } catch (_) {}
    };
  }, [setShowRoleModal, setIntakeMode, setShowElPopover, setShowJDPopover, setConvoStarted])

  // Handle initial session loading from URL
  useEffect(() => {
    if (initialSessionId && userId && chatSessions.length > 0) {
      // Check if the session exists in the fetched sessions
      const sessionExists = chatSessions.find(session => session.id === initialSessionId)
      if (sessionExists) {
        console.log(`Loading initial session from URL: ${initialSessionId}`)
        // Load chat session data (messages, name)
        loadChatSession(initialSessionId)
        // Also load discovery state from the same session data
        loadSessionState(sessionExists)
      } else {
        console.warn(`Session ${initialSessionId} not found in user's sessions`)
        // Remove the invalid sessionId from URL
        const url = new URL(window.location)
        url.searchParams.delete('sessionId')
        window.history.replaceState({}, '', url)
      }
    }
  }, [initialSessionId, userId, chatSessions, loadChatSession, loadSessionState])

  // Update URL when currentSessionId changes
  useEffect(() => {
    if (currentSessionId) {
      const url = new URL(window.location)
      url.searchParams.set('sessionId', currentSessionId)
      window.history.replaceState({}, '', url)
    } else {
      // Remove sessionId from URL when no session is active
      const url = new URL(window.location)
      url.searchParams.delete('sessionId')
      window.history.replaceState({}, '', url)
    }
  }, [currentSessionId])
  
  // Track whether the session state has been loaded
  const sessionStateLoadedRef = useRef(false);
  
  // Load role type and discovery state when currentSessionId changes
  useEffect(() => {
    // Only load the session state if it hasn't been loaded yet or if the session ID has changed
    if (currentSessionId && (!sessionStateLoadedRef.current || sessionStateLoadedRef.current !== currentSessionId)) {
      // Fetch the session data to get role type and discovery state
      fetch(`${API_CONFIG.url}/api/chat-history/${userId}/${currentSessionId}`)
        .then(response => response.json())
        .then(async session => {
          // 🎯 INITIALIZE CENTRAL DISCOVERY QUEUE FIRST (for direct URL access)
          console.log('🔄 INITIALIZING CENTRAL DISCOVERY QUEUE for direct session access:', currentSessionId);
          try {
            await centralDiscoveryQueue.initialize(currentSessionId, []);
            console.log('✅ Central discovery queue initialized for direct session access:', currentSessionId);
            console.log('✅ Central discovery queue session ID set to:', centralDiscoveryQueue.sessionId);
          } catch (error) {
            console.error('❌ Error initializing central discovery queue for direct access:', error);
          }
          
          // Load role type and discovery state from the session
          loadSessionState(session);
          setConvoStarted(true);
          
          // Mark the session state as loaded
          sessionStateLoadedRef.current = currentSessionId;
          
          console.log(`Loaded session ${currentSessionId} on mount`);
          console.log(`Loaded role type: ${session.roleType}, discovery state:`, session.discoveryState || session.discoveryStatus);
        })
        .catch(error => {
          console.error(`Error fetching session data for session ${currentSessionId}:`, error);
        });
    }
  }, [currentSessionId, userId, loadSessionState]);

  // Start the conversation if there are no messages
  useEffect(() => {
    if (messages.length === 0 && !currentSessionId && intakeMode !== 'elevenlabs' && intakeMode !== 'jd' && !showElPopover && !showJDPopover) { // Skip greeting for ElevenLabs/JD so their input is first
      console.log(`🚨 INITIAL MESSAGES USEEFFECT: messages.length is 0, calling handleStartConversation`);
      const initialMessages = handleStartConversation()
      console.log(`🚨 INITIAL MESSAGES: setting ${initialMessages.length} messages`);
      console.log(`🏷️ CALLING setMessages: INITIAL_MESSAGES`);
      setMessages(initialMessages)
      // Don't save the session here to avoid creating empty sessions on page refresh
    } else if (messages.length === 0 && currentSessionId) {
      console.log(`🚫 SKIPPING INITIAL MESSAGES: session ${currentSessionId} is loading, readonly components may be added soon`);
    }
  }, [messages.length, currentSessionId])
  
  // Add event listener for Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showRoleModal) {
        setShowRoleModal(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showRoleModal, setShowRoleModal]);

  // Function to collect component data for session saving
  function getComponentData() {
    return {
      experienceHistogramData,
      levelCounts,
      locationMap,
      locationKeys,
      companyStageData,
      companyBusinessModelData,
      titleKeys,
      selectedSkills,
      confirmedSkills
    }
  }

  // Function to save session name
  function saveSessionName(new_name) {
    // Set the session name first
    setSessionName(new_name)
    // Collect component data
    const componentData = getComponentData()
    // Then save the session with the new name, searchId, and component data
    saveCurrentSession(new_name, sessionSearchId, null, currentDiscoveryState, componentData)
    // Log the operation to help with debugging
    console.log(`Saving session with name: ${new_name}, searchId: ${sessionSearchId}, and component data`)
  }

  // Function to handle session click
  function sessionClick(sessionId) {
    console.log(`Loading session with ID: ${sessionId}`)
    resetMatchResults()
    clearRequirements() // Clear centralized ICP requirements
    
    // Mark the session state as not loaded yet
    sessionStateLoadedRef.current = false;
    
    // Load the chat session
    loadChatSession(sessionId).then(() => {
      console.log(`Chat session ${sessionId} loaded, messages count:`, messages.length);
      console.log(`First few messages:`, messages.slice(0, 3).map(m => ({ role: m.role, content: m.content.substring(0, 50) + '...' })));
      
          // Fetch the session data to get role type and discovery state
          fetch(`${API_CONFIG.url}/api/chat-history/${userId}/${sessionId}`)
            .then(response => response.json())
            .then(async session => {
              // 🎯 INITIALIZE CENTRAL DISCOVERY QUEUE FIRST
              console.log('🔄 INITIALIZING CENTRAL DISCOVERY QUEUE for session:', sessionId);
              try {
                await centralDiscoveryQueue.initialize(sessionId, []);
                console.log('✅ Central discovery queue initialized for session:', sessionId);
              } catch (error) {
                console.error('❌ Error initializing central discovery queue:', error);
              }
              
              // Load role type and discovery state from the session
              loadSessionState(session);
              setConvoStarted(true);

              // Restore saved Findem search ID (ICPID) into hook state if present
              try {
                const restoredIcpId = session?.searchId || session?.sessionSearchId || session?.icpid || session?.icp_id;
                if (restoredIcpId) {
                  setSessionSearchId(restoredIcpId);
                  console.log('🔗 Restored sessionSearchId (ICPID) from session:', restoredIcpId);
                }
              } catch (_) {}
              
              // Restore component data if it exists
              if (session.componentData) {
            console.log(`Restoring component data for session ${sessionId}:`, session.componentData);
            
            // Restore component state using the setter functions from useMessaging
            if (session.componentData.experienceHistogramData) {
              // Note: We can't directly set experienceHistogramData as it's managed internally by useMessaging
              // The data will be restored when the topic decision engine runs
            }
            if (session.componentData.locationMap) {
              // Note: We can't directly set locationMap as it's managed internally by useMessaging
              // The data will be restored when the topic decision engine runs
            }
            if (session.componentData.titleKeys) {
              setTitleKeys(session.componentData.titleKeys);
            }
            if (session.componentData.selectedSkills) {
              setSelectedSkills(session.componentData.selectedSkills);
            }
            if (session.componentData.confirmedSkills) {
              setConfirmedSkills(session.componentData.confirmedSkills);
            }
            
            // Recreate readonly components for all saved component data
            // Use a flag to prevent multiple executions
            if (!sessionStateLoadedRef.current || sessionStateLoadedRef.current !== sessionId) {
              // Check if we have component data to restore
              if (session.componentData) {
                console.log(`Found component data for session ${sessionId}:`, session.componentData);
                
                // The useChatSessionManager hook already loaded the base messages via loadChatSession
                // So we don't need to set session.messages again - it would cause a race condition
                console.log(`ℹ️ Base messages already loaded by useChatSessionManager hook: ${session.messages ? session.messages.length : 0} messages`);
                
                // SIMPLIFIED SESSION RESTORATION: Only show the last completed discovery topic as read-only
                setTimeout(() => {
                  console.log(`🏷️ SIMPLIFIED SESSION RESTORATION for session ${sessionId}`);
                  setMessages(prevMessages => {
                    console.log(`🔧 SIMPLIFIED RESTORATION: prev messages count: ${prevMessages.length}`);
                    
                    // Check for session-restored components to avoid duplicates
                    const hasSessionRestoredComponents = prevMessages.some(msg => 
                      msg.sessionRestored === true && msg.id && msg.id.includes(sessionId)
                    );
                    
                    if (hasSessionRestoredComponents) {
                      console.log(`Skipping restoration - session ${sessionId} already restored`);
                      return prevMessages;
                    }
                    
                    // Determine the last completed discovery topic based on component data
                    let lastCompletedTopic = null;
                    let lastReadOnlyComponent = null;
                    
                    // Check in order of discovery sequence to find the last completed topic
                    if (session.componentData.companyStageData && Object.keys(session.componentData.companyStageData).length > 0) {
                      lastCompletedTopic = 'company_stage';
                      lastReadOnlyComponent = {
                        role: 'AI',
                        content: 'Here are your selected company stages:',
                        component: 'CompanyStageConfirmationReadOnly',
                        componentProps: {
                          stages: Object.keys(session.componentData.companyStageData),
                          stageData: session.componentData.companyStageData
                        },
                        id: `company_stage_readonly_${sessionId}_${Date.now()}`,
                        timestamp: new Date().toISOString(),
                        _hasComponent: true,
                        isReadOnly: true,
                        sessionRestored: true
                      };
                    } else if (session.componentData.locationKeys && session.componentData.locationKeys.length > 0) {
                      lastCompletedTopic = 'location';
                      lastReadOnlyComponent = {
                        role: 'AI',
                        content: 'Here are your selected locations:',
                        component: 'LocationSelectionReadOnly',
                        componentProps: {
                          locationKeys: session.componentData.locationKeys,
                          locationMap: session.componentData.locationMap
                        },
                        id: `location_readonly_${sessionId}_${Date.now()}`,
                        timestamp: new Date().toISOString(),
                        _hasComponent: true,
                        isReadOnly: true,
                        sessionRestored: true
                      };
                    } else if (session.componentData.titleKeys && session.componentData.titleKeys.length > 0) {
                      lastCompletedTopic = 'title';
                      lastReadOnlyComponent = {
                        role: 'AI',
                        content: 'Here are your selected job titles:',
                        component: 'TitleConfirmationReadOnly',
                        componentProps: {
                          titleKeys: session.componentData.titleKeys
                        },
                        id: `titles_readonly_${sessionId}_${Date.now()}`,
                        timestamp: new Date().toISOString(),
                        _hasComponent: true,
                        isReadOnly: true,
                        sessionRestored: true
                      };
                    } else if (session.componentData.confirmedSkills?.length > 0 || session.componentData.selectedSkills?.length > 0 || session.componentData.skills?.length > 0) {
                      lastCompletedTopic = 'skills';
                      let skillsToShow = session.componentData.confirmedSkills?.length > 0 
                        ? session.componentData.confirmedSkills 
                        : session.componentData.selectedSkills;
                      skillsToShow = (skillsToShow && skillsToShow.length > 0) ? skillsToShow : session.componentData?.skills;
                      lastReadOnlyComponent = {
                        role: 'AI',
                        content: '',
                        component: 'SkillsConfirmationReadOnly',
                        componentProps: {
                          skills: skillsToShow
                        },
                        id: `skills_readonly_${sessionId}_${Date.now()}`,
                        timestamp: new Date().toISOString(),
                        _hasComponent: true,
                        isReadOnly: true,
                        sessionRestored: true
                      };
                    }
                    
                    if (lastReadOnlyComponent) {
                      console.log(`✅ SIMPLIFIED RESTORATION: Adding last completed topic '${lastCompletedTopic}' as read-only`);
                      
                      // Add a "Continue Discovery" button after the readonly component
                      const continueDiscoveryButton = {
                        role: 'AI',
                        content: '',
                        component: 'ContinueDiscoveryButton',
                        componentProps: {
                          lastCompletedTopic: lastCompletedTopic,
                          onContinue: () => {
                            console.log('🔄 Continue Discovery clicked, calling advanceToNextTopic');
                            advanceToNextTopic();
                          }
                        },
                        id: `continue_discovery_${sessionId}_${Date.now()}`,
                        timestamp: new Date().toISOString(),
                        _hasComponent: true,
                        isReadOnly: false,
                        sessionRestored: true
                      };
                      
                      // Keep all existing messages and add the last read-only component + continue button
                      const messagesWithLastComponent = [...prevMessages, lastReadOnlyComponent, continueDiscoveryButton];
                      
                      // Store the last completed topic for continue discovery logic
                      window.sessionRestoreInfo = {
                        sessionId: sessionId,
                        lastCompletedTopic: lastCompletedTopic,
                        componentData: session.componentData,
                        timestamp: Date.now()
                      };
                      
                      console.log(`📊 SIMPLIFIED RESTORATION: Returning ${messagesWithLastComponent.length} messages (was ${prevMessages.length})`);
                      return messagesWithLastComponent;
                    }
                    
                    console.log(`⚠️ SIMPLIFIED RESTORATION: No completed topics found for session ${sessionId}`);
                    return prevMessages;
                  });
                }, 100);
              } else {
                console.log(`No component data found for session ${sessionId}`);
              }
            }
          }
          
          // Mark the session state as loaded
          sessionStateLoadedRef.current = sessionId;
          
          // Log that we've loaded a session
          console.log(`Session ${sessionId} loaded, messages will be sent with this context`);
          console.log(`Setting useServerChatHistory flag to true for session ${sessionId}`);
          console.log(`Loaded role type: ${session.roleType}, discovery state:`, session.discoveryState);
          console.log(`Session messages count from API:`, session.messages ? session.messages.length : 0);
          
          console.log(`✅ Session messages and readonly components will be handled above - no separate API overwrite needed`);
          
          // Explicitly call handleRefreshResults after loading a session to fetch matches
          console.log(`Explicitly calling handleRefreshResults for session ${sessionId}`);
          handleRefreshResults(session.messages || messages);
        })
        .catch(error => {
          console.error(`Error fetching session data for session ${sessionId}:`, error);
          setConvoStarted(true); // Still set convoStarted to true even if we couldn't load the session data
        });
    });
  }

  // Function to save and open search
  async function handleSaveOpenSearch() {
    saveOpenSearch(sessionName, (name, id) => {
      setSessionSearchId(id)
      saveCurrentSession(name, id)
    })
  }

  // Function to open search without saving
  function handleOpenFindemSearch() {
    openFindemSearch();
  }

  // Function to clear all chat sessions
  const clearAllSessions = async () => {
    if (window.confirm('Are you sure you want to delete all chat sessions? This action cannot be undone.')) {
      try {
        setMessages([]) // Clear messages immediately
        // Delete each session one by one
        for (const session of chatSessions) {
          try { await centralDiscoveryQueue.resetForSession(session.id); } catch (_) {}
          // Also delete the underlying Findem search (ICPID) if present for this session
          try {
            const icpId =
              session?.searchId ||
              session?.sessionSearchId ||
              session?.icpid ||
              session?.icp_id;
            if (icpId) {
              console.log('🗑️ Deleting Findem search for session:', session.id, 'ICPID:', icpId);
              await deleteIcp(icpId);
            }
          } catch (e) {
            console.error('Failed to delete ICP for session', session.id, e);
          }
          await deleteChatSession(session.id)
        }
        // Start a new session after clearing all

        // Ensure all query params referencing previous sessions are removed
        try {
          const url = new URL(window.location);
          url.searchParams.delete('sessionId');
          window.history.replaceState({}, '', url);
        } catch (_) {}

        startNewSession()
        resetMatchResults() // Reset right sidebar
        resetRoleTypeSelection() // Reset role type selection
        clearRequirements() // Clear centralized ICP requirements
        // Clear client-side API caches and persistent flags
        try { clearClientCaches(); } catch (_) {}
        try { localStorage.removeItem('intakeChoice'); } catch (_) {}
        try { window.__intakeChoice = undefined; window.__jdInFlight = false; } catch (_) {}
        // Clear central discovery queue
        if (window.centralDiscoveryQueue) {
          window.centralDiscoveryQueue.clear();
        }
        // Broadcast UI cache clear event to reset any read-only UI state in chat interfaces
        try { window.dispatchEvent(new CustomEvent('app:clear-cache')); } catch (_) {}
      } catch (error) {
        console.error('Error clearing all sessions:', error)
      }
    }
  }

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        overflow: "hidden", // Prevent body scrolling
        width: "100%",
        maxWidth: "100vw", // Ensure it doesn't exceed viewport width
        overflowX: "hidden", // Explicitly prevent horizontal scrolling
        bgcolor: "#FFFFFF", // Updated to white background
        color: COLORS.text.primary,
        position: "relative"
      }}
    >
      {/* Left Sidebar - Full height */}
      <Sidebar
        side="left"
        isOpen={leftSidebarOpen}
        chatSessions={chatSessions}
        userInfo={userInfo}
        activeSessionId={currentSessionId}
        onNewChat={async () => {
          setMessages([]) // Clear messages immediately
          try { await centralDiscoveryQueue.resetForSession(currentSessionId); } catch (_) {}
          // Clear client-side API caches and persistent flags
          try { clearClientCaches(); } catch (_) {}
          try { localStorage.removeItem('intakeChoice'); } catch (_) {}
          try { window.__intakeChoice = undefined; window.__jdInFlight = false; } catch (_) {}

          // Ensure stale sessionId query param is removed before starting a new intake
          try {
            const url = new URL(window.location);
            url.searchParams.delete('sessionId');
            window.history.replaceState({}, '', url);
          } catch (_) {}

          startNewSession()
          resetMatchResults() // Reset right sidebar
          resetRoleTypeSelection() // Reset role type selection
          clearRequirements() // Clear centralized ICP requirements
          
          // Clear all component data
          setTitleKeys([])
          setSelectedSkills([])
          setConfirmedSkills([])
          
          // Clear central discovery queue
          if (window.centralDiscoveryQueue) {
            window.centralDiscoveryQueue.clear();
          }
          // Broadcast UI cache clear event to reset any read-only UI state in chat interfaces
          try { window.dispatchEvent(new CustomEvent('app:clear-cache')); } catch (_) {}

          // Pre-select default role type silently BEFORE opening selection
          try {
            originalHandleRoleTypeSelect(['TECHNICAL'], { silent: true });
          } catch (_) {}

          // Show intake mode selection (JD or Conversational)
          setShowIntakeModeModal(true)
        }}
        onClickSession={sessionClick}
        onDelete={async (id) => {
          setMessages([]) // Clear messages immediately
          try { await centralDiscoveryQueue.resetForSession(id); } catch (_) {}

          // Proactively clear any lingering sessionId from the URL to avoid reload weirdness
          try {
            const url = new URL(window.location);
            url.searchParams.delete('sessionId');
            window.history.replaceState({}, '', url);
          } catch (_) {}

          // Also delete the underlying Findem search (ICPID) if present for this session
          try {
            // Prefer in-memory state when deleting the current session
            let icpIdToDelete = (id === currentSessionId ? sessionSearchId : undefined);
            if (!icpIdToDelete) {
              // Fallback to lookup in cached sessions list
              const sess = Array.isArray(chatSessions) ? chatSessions.find(s => s.id === id) : null;
              icpIdToDelete =
                sess?.searchId ||
                sess?.sessionSearchId ||
                sess?.icpid ||
                sess?.icp_id;
            }
            if (icpIdToDelete) {
              console.log('🗑️ Deleting Findem search for deleted session:', id, 'ICPID:', icpIdToDelete);
              await deleteIcp(icpIdToDelete);
            }
          } catch (e) {
            console.error('Failed to delete ICP for session', id, e);
          }
          // Clear client-side API caches and persistent flags
          try { clearClientCaches(); } catch (_) {}
          try { localStorage.removeItem('intakeChoice'); } catch (_) {}
          try { window.__intakeChoice = undefined; window.__jdInFlight = false; } catch (_) {}
          deleteChatSession(id)
          startNewSession()
          resetMatchResults() // Reset right sidebar
          resetRoleTypeSelection() // Reset role type selection
          clearRequirements() // Clear centralized ICP requirements
          
          // Clear all component data
          setTitleKeys([])
          setSelectedSkills([])
          setConfirmedSkills([])
          
          // Clear central discovery queue
          if (window.centralDiscoveryQueue) {
            window.centralDiscoveryQueue.clear();
          }
          // Broadcast UI cache clear event to reset any read-only UI state in chat interfaces
          try { window.dispatchEvent(new CustomEvent('app:clear-cache')); } catch (_) {}
        }}
        onClearAllSessions={clearAllSessions}
        onToggle={() => setLeftSidebarOpen(!leftSidebarOpen)}
        anchor="left"
      />
      
      {/* Main content area with header, content, and footer */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          marginLeft: leftSidebarOpen ? `${drawerWidth}px` : `${collapsedWidth}px`,
          // No right margin needed since the right sidebar is now inside MainContent
          transition: 'margin 0.3s ease-in-out',
          bgcolor: "#FFFFFF", // Ensure white background for main content area
        }}
      >
        <Header
          setIsAudioReady={setIsAudioReady}
          isAudioReady={isAudioReady}
          onStartRecording={startRecording}
          onStopRecording={stopRecording}
          isRecording={isRecording}
          setSessionName={saveSessionName}
          sessionName={sessionName}
          onSaveChat={(name) => saveCurrentSession(name, sessionSearchId)}
          userInfo={userInfo}
          rightSidebarOpen={rightSidebarOpen}
          // rightSidebarWidth is now managed in MainContent.jsx
          leftSidebarOpen={leftSidebarOpen}
          drawerWidth={drawerWidth}
          collapsedWidth={collapsedWidth}
          currentDiscoveryState={currentDiscoveryState}
          convoStarted={convoStarted}
          onNavigateToDefineRole={() => {
            // FIRST: Update the discovery state
            const currentTopic = currentDiscoveryState?.currentDiscoTopic;
            const nonDiscoveryTopics = ['candidate_preview', 'messaging']; // Add new UI-only topics here
            
            if (currentTopic && nonDiscoveryTopics.includes(currentTopic)) {
              // Restore the actual discovery topic (the one the user was working on)
              const { discoveryOrder = [], completedTopics = [] } = currentDiscoveryState;
              const currentWorkingTopic = discoveryOrder.find(topic => 
                !nonDiscoveryTopics.includes(topic) && !completedTopics.includes(topic)
              );
              
              console.log('🐛 DEFINE ROLE DEBUG:', {
                currentTopic,
                discoveryOrder,
                completedTopics,
                currentWorkingTopic,
                willUpdateState: !!currentWorkingTopic
              });
              
              if (currentWorkingTopic) {
                updateDiscoveryState({ 
                  ...currentDiscoveryState,
                  currentDiscoTopic: currentWorkingTopic 
                });
              } else {
                console.log('🚨 NO WORKING TOPIC FOUND - all topics might be completed');
              }
            }
            
            // SECOND: Change the UI view after state update (use setTimeout to ensure state update processes first)
            setTimeout(() => {
              if (window.goToIntakeView && typeof window.goToIntakeView === 'function') {
                window.goToIntakeView();
              } else if (window.toggleCandidateViewMode && typeof window.toggleCandidateViewMode === 'function') {
                // Fallback: only toggle if we're currently in candidate view
                if (window.isInCandidateView) {
                  window.toggleCandidateViewMode();
                }
              }
            }, 0);
          }}
          onNavigateToReviewMatches={() => {
            // Navigate to candidate preview - preserve the full discovery state
            updateDiscoveryState({ 
              ...currentDiscoveryState,
              currentDiscoTopic: 'candidate_preview' 
            });
          }}
          onNavigateToSendOutreach={() => {
            // Same behavior as "Done Calibrating" button in ChatInterface
            // Only update the discovery state - let the content change automatically
            updateDiscoveryState({ 
              ...currentDiscoveryState,
              currentDiscoTopic: 'messaging' 
            });
            // Removed window.switchToMessagingTab() to avoid race condition
            // Content should change automatically based on currentDiscoTopic state
          }}
          matchCount={matchCount}
          matchResults={matchResults}
        />
        
        {/* Python Backend Toggle - Hidden for now */}
        {false && (
          <Box
            sx={{
              position: 'absolute',
              top: '10px',
              right: '200px',
              zIndex: 1100,
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              padding: '5px 10px',
              borderRadius: '4px',
            }}
          >
            <span style={{ marginRight: '10px', color: '#fff', fontSize: '14px' }}>
              {usePythonBackend ? 'Python Backend' : 'Node.js Backend'}
            </span>
            <label className="switch">
              <input
                type="checkbox"
                checked={usePythonBackend}
                onChange={() => setUsePythonBackend(!usePythonBackend)}
              />
              <span className="slider round"></span>
            </label>
          </Box>
        )}
        
        <Box
          sx={{
            flexGrow: 1,
            overflow: "auto", // Allow content to scroll
            position: "relative",
            bgcolor: "#FFFFFF", // Ensure white background for content area
            marginTop: "72px", // Add margin to account for fixed header
          }}
        >
            <MainContent
              key={`${messages.length}-${messages.filter(msg => msg.sessionRestored === true).length}`}
              intakeMode={intakeMode}
              leftSidebarOpen={leftSidebarOpen}
              isWaitingForResponse={isWaitingForResponse}
              rightSidebarOpen={rightSidebarOpen}
              messages={messages}
              queries={queries}
              currentAIMessage={currentAIMessage}
              matchCount={matchCount}
              matchesLoading={matchesLoading}
              matchResults={matchResults}
              handleRefreshResults={handleRefreshResults}
              handleRefreshResultsFromQueue={handleRefreshResultsFromQueue}
              onSaveSearch={handleSaveOpenSearch}
              onOpenFindemSearch={handleOpenFindemSearch}
              onSendText={handleSendText}
              onSendVoice={startRecording}
              onStopVoice={stopRecording}
              isRecording={isRecording}
              handleStartIntake={() => {
                try {
                  // Pre-select default role type silently BEFORE opening ElevenLabs
                  originalHandleRoleTypeSelect(['TECHNICAL'], { silent: true });
                } catch (_) {}
                // Mark intake as ElevenLabs (treated as JD) so runAIMatches routes to JD SearchGen
                try { window.__intakeChoice = 'elevenlabs'; localStorage.setItem('intakeChoice', 'elevenlabs'); } catch (_) {}
                setShowElPopover(true);
              }}
              convoStarted={convoStarted}
              currentDiscoveryState={currentDiscoveryState}
              advanceToNextTopic={advanceToNextTopic}
              setMessages={setMessages}
              experienceHistogramData={experienceHistogramData}
              levelCounts={levelCounts}
              locationMap={locationMap}
              locationKeys={locationKeys}
              companyStageData={companyStageData}
              companyBusinessModelData={companyBusinessModelData}
              titleKeys={titleKeys}
              setTitleKeys={setTitleKeys}
              selectedSkills={selectedSkills}
              setSelectedSkills={setSelectedSkills}
              confirmedSkills={confirmedSkills}
              setConfirmedSkills={setConfirmedSkills}
              icpRequirements={icpRequirements}
              drawerWidth={drawerWidth}
              collapsedWidth={collapsedWidth}
              userInfo={userInfo}
              userId={userId}
              sessionSearchId={sessionSearchId}
              currentSessionId={currentSessionId}
              sessionName={sessionName}
              onToggle={() => setRightSidebarOpen(!rightSidebarOpen)}
              updateDiscoveryState={updateDiscoveryState}
              onToggleCandidateView={(toggleFn, goToIntakeFn) => {
                // Store the toggle function from MainContent
                window.toggleCandidateViewMode = toggleFn;
                window.goToIntakeView = goToIntakeFn;
              }}
            />
        </Box>
      </Box>
      
      {/* ElevenLabs Intake Popover (API-first UX) */}
      {showElPopover && (
        <ElevenLabsIntakePopover
          open={showElPopover}
          onClose={() => {
            setShowElPopover(false);
            setIntakeMode(null);
            // Restore start screen CTA if needed
            if (!currentSessionId) {
              const hasWelcome =
                Array.isArray(messages) &&
                messages.some(
                  (m) =>
                    m?.role === 'AI' &&
                    typeof m.content === 'string' &&
                    m.content.includes('Ready to begin?')
                );
              if (messages.length === 0 || !hasWelcome) {
                try {
                  const initialMessages = handleStartConversation();
                  setMessages(initialMessages);
                } catch (_) {}
              }
            }
            // Always mark not-started so inline CTAs render under the greeting
            setConvoStarted(false);
          }}
          onConfirm={(transcriptText /* string */, turns /* array */) => {
            try {
              setShowElPopover(false);
              setConvoStarted(true);
              // Gate UI/queue updates until JD SearchGen returns
              try { if (typeof window !== 'undefined') window.__jdInFlight = true; } catch (_) {}

              // Ensure a default role type is selected when none was explicitly chosen
              // so discovery order and state are initialized correctly.
              if (!currentDiscoveryState?.roleType) {
                try {
                  handleRoleTypeSelect(['TECHNICAL'], { silent: true });
                } catch (_) {}
              }

              // Normalize ElevenLabs turns into our chat message shape and append all to the feed.
              const nowTs = Date.now();
              const nowISO = new Date().toISOString();
              const hasTurns = Array.isArray(turns) && turns.length > 0;

              // Build normalized messages:
              // 1) Prefer structured turns from the popover
              // 2) If absent, parse transcriptText by lines like "User: ..." | "Agent: ..."
              let normalized = [];
              
              if (hasTurns) {
                normalized = turns
                  .map((t, idx) => {
                    const roleRaw = (t?.role || '').toString().toLowerCase();
                    const text =
                      t?.text ||
                      t?.message ||
                      t?.transcript ||
                      (t?.data && (t.data.text || t.data.message)) ||
                      (t?.content && (t.content.text || t.content.message)) ||
                      '';
                    return {
                      role: roleRaw === 'agent' ? 'AI' : 'User',
                      content: String(text || '').trim(),
                      id: `el_turn_${nowTs}_${idx}`,
                      timestamp: nowISO
                    };
                  })
                  .filter((m) => m.content && m.content.length > 0);
              } else if (typeof transcriptText === 'string' && transcriptText.trim().length > 0) {
                const lines = transcriptText
                  .split(/\r?\n/)
                  .map((l) => l.trim())
                  .filter(Boolean);
                
                normalized = lines.map((line, idx) => {
                  let role = 'User';
                  let content = line;
                  
                  // Detect leading role prefixes and strip them
                  const m = line.match(/^(User|Agent)\s*:\s*(.*)$/i);
                  if (m) {
                    role = m[1].toLowerCase() === 'agent' ? 'AI' : 'User';
                    content = m[2];
                  } else if (/^agent\s+/i.test(line)) {
                    role = 'AI';
                    content = line.replace(/^agent\s+/i, '');
                  } else if (/^user\s+/i.test(line)) {
                    role = 'User';
                    content = line.replace(/^user\s+/i, '');
                  } else {
                    // If no explicit prefix, alternate sensibly; default first line to AI if it looks like a greeting
                    if (idx === 0 && /thanks|hello|hi|hey|how/i.test(line)) role = 'AI';
                    else role = idx % 2 === 0 ? 'User' : 'AI';
                  }
                  
                  return {
                    role,
                    content: String(content || '').trim(),
                    id: `el_line_${nowTs}_${idx}`,
                    timestamp: nowISO
                  };
                }).filter((m) => m.content && m.content.length > 0);
              } else {
                // No fallback: if we couldn't extract any content, don't inject placeholder bubbles.
                normalized = [];
              }

              // Avoid placeholder artifacts like "Conversation captured."
              const isPlaceholder = (s) => {
                const t = (s || '').trim().toLowerCase();
                return !t || t === 'conversation captured.' || t === 'conversation captured';
              };

              // If we failed to extract anything meaningful, surface a visible notice so it's clear why nothing appeared
              if (!normalized.length && isPlaceholder(transcriptText)) {
                const notice = {
                  role: 'AI',
                  content: 'No transcript detected from ElevenLabs widget.',
                  id: `el_import_empty_${nowTs}`,
                  timestamp: nowISO
                };
                setMessages((prev) => [...prev, notice]);
              } else {
                // Append the conversation turns in order so they render in our chat UI
                if (normalized.length) {
                  // Add a small import notice + turns to make success visible immediately
                  setMessages((prev) => [...prev, ...normalized]);
                } else if (typeof transcriptText === 'string' && transcriptText.trim().length > 0) {
                  // Add a small import notice + turns to make success visible immediately
                  const alt = transcriptText
                    .split(/\r?\n/)
                    .map((l) => l.trim())
                    .filter(Boolean)
                    .map((line, idx) => ({
                      role: idx % 2 === 0 ? 'User' : 'AI',
                      content: line,
                      id: `el_alt_${nowTs}_${idx}`,
                      timestamp: nowISO
                    }));
                  if (alt.length) {
                    setMessages((prev) => [...prev, ...alt]);
                    normalized = alt;
                  }
                } else if (Array.isArray(turns) && turns.length === 0) {
                  // As a last resort if we somehow received empty data, surface a visible debug hint once
                  const notice = {
                    role: 'AI',
                    content: 'No transcript detected from ElevenLabs widget.',
                    id: `el_import_empty_${nowTs}`,
                    timestamp: nowISO
                  };
                  setMessages((prev) => [...prev, notice]);
                } else {
                  // As a light fallback, split by lines and alternate roles for visibility
                  const alt = transcriptText
                    .split(/\r?\n/)
                    .map((l) => l.trim())
                    .filter(Boolean)
                    .map((line, idx) => ({
                      role: idx % 2 === 0 ? 'User' : 'AI',
                      content: line,
                      id: `el_alt_${nowTs}_${idx}`,
                      timestamp: nowISO
                    }));
                  if (alt.length) {
                    setMessages((prev) => [...prev, ...alt]);
                    normalized = alt;
                  }
                }

                // Build a robust aggregated transcript to seed the existing pipeline
                let submitText =
                  typeof transcriptText === 'string' && transcriptText.trim().length > 0
                    ? transcriptText
                    : normalized
                        .map((m) => `${m.role === 'AI' ? 'Agent' : 'User'}: ${m.content}`)
                        .join('\n');

                // If submitText is still placeholder/empty, skip sending into the pipeline
                if (isPlaceholder(submitText)) {
                  submitText = '';
                }

                // Kick off the existing text pipeline (classification, queue init, etc.)
                if (submitText && submitText.trim().length > 0) {
                  // Feed transcript into pipeline as if typed, using JD intake
                  try { window.__intakeChoice = 'elevenlabs'; localStorage.setItem('intakeChoice', 'elevenlabs'); } catch (_) {}
                  // Seed central discovery queue immediately from JD path so UI shows completed topics
                  handleRefreshResults([...messages, ...normalized], undefined, { intakeType: 'jd' });
                  handleSendText({ text: submitText, skipRenderUserBubble: true });
                  return;
                  // const pendingId = `el_jd_parse_${nowTs}`;
                  try {
                    setMessages((prev) => [
                      ...prev,
                      {
                        role: 'AI',
                        content: 'Parsing job description...',
                        id: pendingId,
                        timestamp: new Date().toISOString()
                      }
                    ]);

                    (async () => {
                      try {
                        const resp = await fetch('https://search.findem.ai/pub/api/copilot-jd-searchgen', {
                          method: 'POST',
                          mode: 'cors',
                          credentials: 'include',
                          headers: {
                            'content-type': 'application/json'
                          },
                          body: JSON.stringify({
                            jd_search_gen_info: {
                              verbose: false,
                              jd_source: submitText
                            },
                            reset_context: true
                          })
                        });

                        let parsed = null;
                        try {
                          parsed = await resp.json();
                        } catch (_) {
                          parsed = null;
                        }

                        setMessages((prev) => {
                          const withoutPending = prev.filter(m => m.id !== pendingId);
                          const preview = parsed ? JSON.stringify(parsed).slice(0, 1200) : `HTTP ${resp.status}`;
                          return [
                            ...withoutPending,
                            {
                              role: 'AI',
                              content: parsed
                                ? 'JD parsed successfully via Copilot JD SearchGen.'
                                : 'JD parsing attempted via Copilot JD SearchGen.',
                              id: `${pendingId}_done`,
                              timestamp: new Date().toISOString()
                            },
                            {
                              role: 'AI',
                              content: 'SearchGen preview:\n' + preview + (parsed && JSON.stringify(parsed).length > 1200 ? ' …' : ''),
                              id: `${pendingId}_preview`,
                              timestamp: new Date().toISOString()
                            }
                          ];
                        });
                      } catch (err) {
                        console.error('Copilot JD SearchGen error (EL transcript):', err);
                        setMessages((prev) => {
                          const withoutPending = prev.filter(m => m.id !== pendingId);
                          return [
                            ...withoutPending,
                            {
                              role: 'AI',
                              content: `JD parsing failed: ${err?.message || 'Unknown error'}`,
                              id: `${pendingId}_error`,
                              timestamp: new Date().toISOString()
                            }
                          ];
                        });
                      } finally {
                        // Continue into the existing intake pipeline with the transcript
                        setTimeout(() => {
                          try {
                            handleSendText({ text: submitText, skipRenderUserBubble: true });
                          } catch (_) {}
                        }, 50);
                      }
                    })();
                  } catch (_) {
                    // Fallback: if something above throws synchronously, still proceed
                    setTimeout(() => {
                      try {
                        handleSendText({ text: submitText, skipRenderUserBubble: true });
                      } catch (_) {}
                    }, 50);
                  }
                }
              }
            } catch (e) {
              console.error('onConfirm processing error:', e);
              setShowElPopover(false);
            }
          }}
        />
      )}

      {/* JD Intake Popover */}
      {showJDPopover && (
        <JDIntakePopover
          open={showJDPopover}
          onClose={() => {
            setShowJDPopover(false);
            setIntakeMode(null);
            // Restore start screen CTA if needed
            if (!currentSessionId) {
              const hasWelcome =
                Array.isArray(messages) &&
                messages.some(
                  (m) =>
                    m?.role === 'AI' &&
                    typeof m.content === 'string' &&
                    m.content.includes('Ready to begin?')
                );
              if (messages.length === 0 || !hasWelcome) {
                try {
                  const initialMessages = handleStartConversation();
                  setMessages(initialMessages);
                } catch (_) {}
              }
            }
            // Always mark not-started so inline CTAs render under the greeting
            setConvoStarted(false);
          }}
          onConfirm={async (jdText /* string */) => {
            try {
              setShowJDPopover(false);
              setConvoStarted(true);

              // Ensure a default role type is selected when none was explicitly chosen
              if (!currentDiscoveryState?.roleType) {
                try {
                  handleRoleTypeSelect(['TECHNICAL'], { silent: true });
                } catch (_) {}
              }

              const txt = (jdText || '').toString().trim();
              if (txt.length > 0) {
                // Gate UI/queue updates until JD SearchGen returns
                try { if (typeof window !== 'undefined') window.__jdInFlight = true; } catch (_) {}
                const nowTs = Date.now();
                const nowISO = new Date().toISOString();

                // Insert JD as the very first user chat content
                setMessages((prev) => [
                  ...prev,
                  {
                    role: 'User',
                    content: txt,
                    id: `jd_${nowTs}`,
                    timestamp: nowISO
                  }
                ]);

                // Feed JD into pipeline as if typed, using JD intake
                try {
                  try { window.__intakeChoice = 'jd'; localStorage.setItem('intakeChoice', 'jd'); } catch (_) {}
                  // Seed central discovery queue immediately from JD path so UI shows completed topics
                  handleRefreshResults([...messages, { role: 'User', content: txt, id: `jd_${nowTs}`, timestamp: nowISO }], undefined, { intakeType: 'jd' });
                  handleSendText({ text: txt, skipRenderUserBubble: true, skipTopicDecisionEngine: false });
                  return;

                  let parsed = null;
                  try {
                    parsed = await resp.json();
                  } catch (_) {
                    parsed = null;
                  }

                  // Replace pending message with result summary
                  setMessages((prev) => {
                    const withoutPending = prev.filter(m => m.id !== `jd_parse_pending_${nowTs}`);
                    const preview = parsed ? JSON.stringify(parsed).slice(0, 1200) : `HTTP ${resp.status}`;
                    return [
                      ...withoutPending,
                      {
                        role: 'AI',
                        content: parsed
                          ? 'JD parsed successfully via Copilot JD SearchGen.'
                          : 'JD parsing attempted via Copilot JD SearchGen.',
                        id: `jd_parse_done_${nowTs}`,
                        timestamp: new Date().toISOString()
                      },
                      // Optional: include a compact preview of the response for visibility
                      {
                        role: 'AI',
                        content: 'SearchGen preview:\n' + preview + (parsed && JSON.stringify(parsed).length > 1200 ? ' …' : ''),
                        id: `jd_parse_preview_${nowTs}`,
                        timestamp: new Date().toISOString()
                      }
                    ];
                  });
                } catch (err) {
                  console.error('Copilot JD SearchGen error:', err);
                  // Replace pending with error
                  setMessages((prev) => {
                    const withoutPending = prev.filter(m => m.id !== `jd_parse_pending_${nowTs}`);
                    return [
                      ...withoutPending,
                      {
                        role: 'AI',
                        content: `JD parsing failed: ${err?.message || 'Unknown error'}`,
                        id: `jd_parse_error_${nowTs}`,
                        timestamp: new Date().toISOString()
                      }
                    ];
                  });
                }

                // Continue into the existing text pipeline (classification, queue init, etc.)
                setTimeout(() => {
                  try {
                    // Pass through the JD text so downstream logic can proceed as before
                    handleSendText({ text: txt, skipRenderUserBubble: true, skipTopicDecisionEngine: false });
                  } catch (_) {}
                }, 50);
              }
            } catch (e) {
              console.error('JD onConfirm error:', e);
              setShowJDPopover(false);
            }
          }}
        />
      )}

      {/* Intake Mode Selection Modal */}
      {showIntakeModeModal && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          }}
        >
          <IntakeModeSelection
            onSelectMode={(mode) => {
              // Close the mode picker
              setShowIntakeModeModal(false);

              // Do NOT open any popovers here.
              // Drop user onto the greeting screen with inline "Start Fresh" / "Start with JD".
              // Pre-select default role type silently so discovery order is initialized.
              try {
                originalHandleRoleTypeSelect(['TECHNICAL'], { silent: true });
              } catch (_) {}

              // Ensure we render the greeting inline choices
              setConvoStarted(false);
              setIntakeMode(null);

              // Make sure the greeting exists (if not already injected)
              if (!currentSessionId) {
                const hasWelcome =
                  Array.isArray(messages) &&
                  messages.some(
                    (m) =>
                      m?.role === 'AI' &&
                      typeof m.content === 'string' &&
                      m.content.includes('Ready to begin?')
                  );
                if (messages.length === 0 || !hasWelcome) {
                  try {
                    const initialMessages = handleStartConversation();
                    setMessages(initialMessages);
                  } catch (_) {}
                }
              }
            }}
            onClose={() => setShowIntakeModeModal(false)}
          />
        </Box>
      )}

      {/* Role Selection Modal - Updated to cover header and adjust spacing */}
      {showRoleModal && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1200, // Increased z-index to ensure it's above the header
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          }}
        >
          <RoleTypeSelection 
            onRoleTypeSelect={handleRoleTypeSelect} 
            onClose={() => setShowRoleModal(false)} 
          />
        </Box>
      )}
    </Box>
  );
};

export default AppLayout;
